import { Link } from "react-router-dom";
import { CartItem } from "@/mocks/cart";

interface Props {
  items: CartItem[];
  total: number;
  orderId: string;
  address: {
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    email: string;
  };
}

export default function OrderConfirmation({ items, total, orderId, address }: Props) {
  const sellers = [...new Set(items.map((i) => i.seller))];

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center py-16 px-4">
      <div className="w-full max-w-lg text-center">
        {/* Success Icon */}
        <div className="w-20 h-20 flex items-center justify-center bg-green-50 rounded-full mx-auto mb-6">
          <i className="ri-checkbox-circle-fill text-4xl text-green-500" />
        </div>
        <h1 className="text-2xl font-extrabold text-gray-900 mb-2">Order Confirmed!</h1>
        <p className="text-gray-500 text-sm mb-1">Your order has been placed successfully.</p>
        <p className="text-xs text-gray-400 mb-8">Order ID: <span className="font-semibold text-gray-700">{orderId}</span></p>

        {/* Seller Notification Banner */}
        <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 mb-6 text-left">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-full flex-shrink-0 mt-0.5">
              <i className="ri-notification-3-fill text-orange-500 text-sm" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-1">Sellers have been notified</p>
              <p className="text-xs text-gray-500 leading-relaxed">
                {sellers.join(", ")} {sellers.length > 1 ? "have" : "has"} received your order and will begin processing it shortly. You&apos;ll receive a tracking number via email once shipped.
              </p>
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 mb-6 text-left">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Order Summary</h3>
          <div className="space-y-3 mb-4">
            {items.map((item) => (
              <div key={item.id} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-50">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-800 line-clamp-1">{item.title}</p>
                  <p className="text-xs text-gray-400">Qty: {item.quantity}</p>
                </div>
                <span className="text-xs font-semibold text-gray-900">${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-100 pt-3 space-y-1.5">
            <div className="flex justify-between text-xs text-gray-500">
              <span>Subtotal</span><span>${total.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>Shipping</span><span className="text-green-600">Free</span>
            </div>
            <div className="flex justify-between text-sm font-bold text-gray-900 pt-1">
              <span>Total</span><span>${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Delivery Address */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 mb-8 text-left">
          <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
            <i className="ri-map-pin-line text-orange-500" /> Delivery Address
          </h3>
          <p className="text-sm text-gray-700">{address.firstName} {address.lastName}</p>
          <p className="text-xs text-gray-500">{address.address}</p>
          <p className="text-xs text-gray-500">{address.city}, {address.state} {address.zip}</p>
          <p className="text-xs text-gray-500">{address.country}</p>
          <p className="text-xs text-gray-400 mt-2">Confirmation sent to: <span className="text-gray-600">{address.email}</span></p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/marketplace" className="flex-1 py-3 bg-orange-500 text-white text-sm font-semibold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap text-center">
            Continue Shopping
          </Link>
          <Link to="/dashboard" className="flex-1 py-3 border border-gray-200 text-gray-700 text-sm font-semibold rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-center">
            View Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
